// Import dependencies
import { html, LitElement } from 'lit';
import { customElement, query, property } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
const { isEmpty, htmlEntities } = _uo.utility;

// Translatable strings
const strings = UncannyAutomator._core.i18n.components.itemLoop;

// eslint-disable-next-line require-jsdoc
@customElement( 'uap-builder-item-loop-filter-edit-dialog' )
export class AutomatorBuilderItemLoopFilterEditDialog extends LitElement {
	// Properties
	// The unique ID of the loop (parent of this loop filter)
	@property( { type: Number, attribute: 'loop-id' } ) accessor loopID;

	// The unique ID of this loop filter
	@property( { type: Number, attribute: 'loop-filter-id' } ) accessor loopFilterID;

	// The code of the loop filter
	@property( { type: String, attribute: 'loop-filter-code' } ) accessor loopFilterCode;

	// The code of the integration
	@property( { type: String, attribute: 'integration-code' } ) accessor integrationCode;

	// The loop fields
	@property( { type: Object, attribute: false } ) accessor fields = {};

	// Returns the component of the sentence
	// We'll use this to save it as a backup for the logs
	@query( 'uap-builder-item-sentence' ) accessor $sentence;

	// Returns the component that has the fields of this loop filter
	// We'll use it to retrieve the values
	@query( 'uap-builder-item-legacy-form' ) accessor $optionsForm;

	/**
	 * Disable the shadow DOM
	 *
	 * @todo Remove this method once fields become web components. This component should be using the shadow DOM.
	 * @return {Element} The element
	 */
	createRenderRoot() {
		return this;
	}

	// eslint-disable-next-line require-jsdoc
	render() {
		return html`
			<uo-dialog
				heading=${ strings.configureTheFilter }

				size="large"
				force-manual-close
				open

				@uap-dialog-close=${ this.handleDialogClose }
			>
				${ this._templateHeader }

				<uap-builder-item-legacy-form
					item-id=${ ifDefined( this.loopFilterID ) }
					item-type="loop-filter"

					.fieldsDirectory=${
						// Merge all fields in a single group. We don't need groups here yet, because the sentence
						// is not rendering sentence pills
						Object.values( this.directoryItemData.fields )
							.reduce( ( acc, val ) => acc.concat( val ), [] )
					}
					
					.fieldsValues=${ this.fields }

					return-format="loop-filter"

					@uap-builder-legacy-form-change=${ ( event ) => {
						// Update the fields, even if they are not saved yet
						// The cancel button will revert the changes, and the save button will save them
						this.fields = event.detail.unsavedFieldValues;
					} }
				></uap-builder-item-legacy-form>

				<uo-button
					slot="primary-action"

					@click=${ this.handleConfirmEdit }
				>
					${ strings.confirm }
				</uo-button>

				<uo-button
					slot="secondary-action"
					dialog-action="close"
					color="secondary"
				>
					${ strings.cancel }
				</uo-button>
			</uo-dialog>
		`;
	}

	/**
	 * Handles updating the filter data on the server
	 *
	 * @return {Promise|undefined} The promise that either updates or creates the loop filter
	 */
	handleConfirmEdit() {
		// Check if the fields are available
		if ( isEmpty( this.$optionsForm ) ) {
			return;
		}

		// Validate the form
		if ( ! this.$optionsForm.areFieldsValid ) {
			return;
		}

		// Update the sentence before saving it.
		// Although the sentence will be updated when a field is changed, it is possible that the user will open the
		// dialog and save it without making any modifications. In that case, unless we add this step, the sentence
		// will be outdated.
		this.requestSentenceUpdate();

		// The loop filter already exists, just update it
		if ( this.mode === 'edit' ) {
			// Update data
			return UncannyAutomator._core.recipe.item.loop.updateLoopFilter(
				this.loopID,
				this.loopFilterID,
				this.fieldsForRemote,
				this.backupForRemote
			).then( response => {
				// Delete this component
				this.handleDialogClose();
			} );
		}

		// If the loop filter doesn't exist, create it and assign the current fields and backup
		if ( this.mode === 'create' ) {
			return UncannyAutomator._core.recipe.item.loop.createLoopFilter(
				this.loopID,
				this.integrationCode,
				this.loopFilterCode,
				this.fieldsForRemote,
				this.backupForRemote
			).then( response => {
				// Delete this component
				this.handleDialogClose();
			} );
		}
	}

	/**
	 * Request the form inside the dialog to update the unsaved fields of the dialog
	 */
	requestSentenceUpdate() {
		this.fields = this.$optionsForm.unsavedFieldValues;
	}

	/**
	 * Renders the header of the dialog
	 *
	 * @return {TemplateResult} The header
	 */
	get _templateHeader() {
		return html`
			<div class="uap-edit-dialog-header">
				<div class="uap-edit-dialog-header-integration-icon">
					<uap-log-integration-icon 
						size="24" 
						integration-id="${ this.integrationCode }"
					></uap-log-integration-icon>
				</div>

				<div class="uap-edit-dialog-header-tags">
					<uo-chip 
						size="small" 
						variant="pill" 
						filled
					>
						${ UncannyAutomator._integrations[ this.integrationCode ].name }
					</uo-chip>
				</div>

				<div class="uap-edit-dialog-header-filter-sentence">
					<uap-builder-item-sentence
						item-id=${ this.loopFilterID }
						.sentence=${ this.directoryItemData.sentence_readable }
						.fields=${ this.fields }
					></uap-builder-item-sentence>
				</div>
			</div>
		`;
	}

	/**
	 * Returns the global/directory data of this item
	 *
	 * @return {Object} The directory data of this item. This contains, for example, the fields attributes
	 */
	get directoryItemData() {
		return UncannyAutomator._core.directory.items.getLoopFilter(
			this.integrationCode,
			this.loopFilterCode
		);
	}

	/**
	 * Returns the global/directory data of the integration of this loop filter
	 *
	 * @return {Object} The directory data of this loop filter. Contains, for example, the name of the integration.
	 */
	get directoryIntegrationData() {
		return UncannyAutomator._core.directory.integrations.getIntegration( this.integrationCode );
	}

	/**
	 * Creates the object that'll be saved in the database containing the values of the fields
	 *
	 * @return {Object|undefined} The fields
	 */
	get fieldsForRemote() {
		if ( ! isEmpty( this.$optionsForm ) ) {
			return JSON.stringify( this.$optionsForm.unsavedFieldValues );
		}
	}

	/**
	 * Creates the object that'll be saved in the database containing the backup
	 *
	 * @return {Object} The backup
	 */
	get backupForRemote() {
		return JSON.stringify( {
			// The integration name. e.g. "WordPress"
			// eslint-disable-next-line camelcase
			integration_name: this.directoryIntegrationData.name,

			// The sentence of the action, without the values of the fields
			sentence: this.directoryItemData.sentence_readable,

			// The sentence with field values, HTML. Already encoded
			// eslint-disable-next-line camelcase
			sentence_html: htmlEntities.encode( this.$sentence.outputForLogs )
		} )
	}

	/**
	 * Gets the mode of the dialog.
	 * If the `.loopFilterID` is not defined, it means the loop filter doesn't exist yet and we're on "Edit" mode
	 *
	 * @return {String} The mode
	 */
	get mode() {
		return isEmpty( this.loopFilterID ) ? 'create' : 'edit';
	}

	/**
	 * Delete this component when something closes the dialog
	 */
	handleDialogClose() {
		this.remove();
	}
}